import {
  AlertOutlineIcon,
  CheckCircleIcon,
  FileDocumentIcon,
  InformationIcon,
} from '@atgfr8/icons';
import { Button } from '@components';
import { openModal } from '@store/modals';
import {
  isCreditLimitExceeded,
  orderContainsLTLLoad,
  orderStore,
} from '@store/orders';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatAmount } from '@utils/utils';
import { DateTime } from 'luxon';
import { Show } from 'solid-js';

import {
  CreditIncreaseRequestModalId,
  creditIncreaseRequestForCurrentOrder,
} from '../../models/CreditIncreaseRequestModal';
import classes from './classes';

export const CreditLimitBanner = () => {
  const handleRequestMoreCredit = () => {
    openModal(CreditIncreaseRequestModalId);
  };

  const creditRequest = () => creditIncreaseRequestForCurrentOrder();
  const creditRequestSent = () => creditRequest() !== null;

  const requestTimestamp = () => {
    const requestedAt = creditRequest()?.requestedAt;
    if (!requestedAt) return '';
    const parsed = DateTime.fromISO(requestedAt);
    return parsed.isValid ? parsed.toFormat('MM/dd/yyyy hh:mm a') : '';
  };

  const requestUser = () => creditRequest()?.requestedByUserName ?? '';
  const requestedAmount = () => creditRequest()?.amount ?? null;

  const liabilityDecision = () =>
    orderStore.order.negativeCreditLiabilityDecision;

  const liabilityAccepted = () =>
    liabilityDecision()?.userAcceptedLiability === true;

  const showInfoVariant = () =>
    !isCreditLimitExceeded() &&
    !orderContainsLTLLoad() &&
    liabilityAccepted() &&
    creditRequestSent();

  const showRequestOnlyVariant = () =>
    !isCreditLimitExceeded() &&
    !orderContainsLTLLoad() &&
    !liabilityAccepted() &&
    creditRequestSent();

  const showWarningVariant = () =>
    !isCreditLimitExceeded() &&
    !orderContainsLTLLoad() &&
    liabilityAccepted() &&
    !creditRequestSent();

  const showOrangePalette = () =>
    showWarningVariant() || showRequestOnlyVariant();

  const acceptedTimestamp = () => {
    const dt = liabilityDecision()?.negativeCreditAcceptanceDateTime;
    if (!dt) return '';
    const parsed = DateTime.fromISO(dt);
    return parsed.isValid ? parsed.toFormat('MM/dd/yyyy hh:mm a') : '';
  };

  const containerClass = (): string => {
    if (showInfoVariant()) {
      return 'flex items-center mt-2 mb-8 gap-2 p-3 rounded bg-[#E5F2FB] border border-[#0070A2]';
    }
    if (showOrangePalette()) return classes.bannerContainerWarning;
    return classes.bannerContainer;
  };

  const titleClass = (): string => {
    if (showInfoVariant()) {
      return 'flex items-center gap-2 text-[#014361] font-semibold text-sm';
    }
    if (showOrangePalette()) return classes.bannerTitleWarning;
    return classes.bannerTitle;
  };

  const titleText = () => {
    if (showInfoVariant())
      return 'Credit Limit Exceeded - Additional Credit Requested';
    if (showRequestOnlyVariant())
      return 'Credit Request Submitted - Additional Credit Requested';
    if (showWarningVariant())
      return 'Credit Limit Exceeded \u00B7 Liability Accepted';
    return 'Credit Limit Exceeded';
  };

  return (
    <div class={containerClass()} data-testid="credit-limit-exceeded-banner">
      <Show
        when={showInfoVariant()}
        fallback={
          <AlertOutlineIcon
            style={{
              'font-size': '20px',
              color: showOrangePalette() ? '#ED6C02' : '#B00020',
            }}
          />
        }
      >
        <InformationIcon style={{ 'font-size': '20px', color: '#0070A2' }} />
      </Show>
      <div class={classes.bannerBody}>
        <div class={titleClass()}>{titleText()}</div>
        <div class={classes.bannerInfo}>
          <Show when={showInfoVariant()}>
            Agent accepted liability for this order's negative credit balance.
            Additional credit of{' '}
            <span class="font-semibold">
              {formatAmount(requestedAmount() ?? 0)}
            </span>{' '}
            has been requested and is pending Credit Team review.
          </Show>
          <Show when={showRequestOnlyVariant()}>
            A carrier cannot be assigned to the load(s) on this order until the
            additional credit has been approved by the Credit Team.
          </Show>
          <Show when={showWarningVariant()}>
            Agent has acknowledged and accepted responsibility for the negative
            credit balance on this order. No additional credit has been
            requested.
          </Show>
          <Show
            when={
              !showInfoVariant() &&
              !showWarningVariant() &&
              !showRequestOnlyVariant()
            }
          >
            This order exceeds the customer's approved credit limit. A carrier
            cannot be assigned to this load until additional credit has been
            approved by the Credit Team.
          </Show>
        </div>
        <Show when={liabilityAccepted()}>
          <div class={classes.bannerInfo}>
            Accepted by:{' '}
            <span class="font-semibold">
              {liabilityDecision()?.acceptedByUserName}
            </span>{' '}
            <span class="font-semibold">{acceptedTimestamp()}</span>
          </div>
        </Show>
        <Show when={showInfoVariant()}>
          <div class={classes.bannerInfo}>
            Credit Requested <span class="font-semibold">{requestUser()}</span>{' '}
            <span class="font-semibold">{requestTimestamp()}</span>
          </div>
        </Show>
        <Show when={showRequestOnlyVariant()}>
          <div class={classes.bannerInfo}>
            Request Submitted by{' '}
            <span class="font-semibold">{requestUser()}</span>{' '}
            <span class="font-semibold">{requestTimestamp()}</span>
          </div>
        </Show>
        <Show
          when={
            !showInfoVariant() &&
            !showRequestOnlyVariant() &&
            creditRequestSent()
          }
        >
          <div class={classes.bannerInfo}>
            Credit request submitted on {requestTimestamp()} by {requestUser()}
          </div>
        </Show>
        <Show when={!showInfoVariant()}>
          <div class={classes.bannerActionsContainer}>
            <Show
              when={!creditRequestSent()}
              fallback={
                <div class="flex items-center gap-1">
                  <CheckCircleIcon
                    style={{ 'font-size': '16px', color: '#248670' }}
                  />
                  <span class="text-xs text-[#248670] font-semibold">
                    Credit Request Sent
                  </span>
                </div>
              }
            >
              <Button
                label="Request Additional Credit"
                variant="outlined"
                size="sm"
                startIcon={<FileDocumentIcon style={{ 'font-size': '16px' }} />}
                onClick={handleRequestMoreCredit}
                dataTestId={formatDataTestId(
                  'order-request-more-credit',
                  'button',
                )}
                class={classes.requestCreditButton}
              />
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
};
